import { useEffect, useState, useCallback } from 'react';
import get from 'lodash/get';
import map from 'lodash/map';
import find from 'lodash/find';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import qs from 'qs';
import { catalogApi, tenantApi, deliveryApi } from '@vmos2/core';
import { timeUtils } from '@vmos2/utils';

export const useStores = channel => {
  const [storesAvailability, setStoresAvailability] = useState(false);
  const [stores, setStores] = useState([]);
  const [deliverooStores, setDeliverooStores] = useState([]);
  const [isDeliveroo, setIsDeliveroo] = useState(false);
  const [menus, setMenus] = useState({});
  const isDelivery = channel === 'delivery';

  useEffect(() => {
    tenantApi.get('stores/availability', { skipAuth: true }).then(result => {
      setStoresAvailability(get(result, 'status', false));
    });
  }, [channel]);

  const getDeliverooStores = async ({ stores, deliverooSignature, lat, lon }) => {
    const deliverooList = await deliveryApi.get('restaurants', {
      params: { lon, lat, provider: 'deliverooSignature' },
    });

    const deliverooLocations = get(deliverooSignature, 'locations');

    const availableStores = map(deliverooLocations, (id, uuid) => {
      const store = find(stores, { uuid });
      const deliverooInfo = find(deliverooList, { id });

      if (store && get(deliverooInfo, 'visible')) {
        return { ...store, deliverooInfo };
      }

      return null;
    });

    return compact(availableStores);
  };

  const getStores = useCallback(
    async (params, settings) => {
      if (!params.offset) {
        setStores([]);
      }

      const deliverooSignature = get(settings, ['deliveryIntegration', 'deliverooSignature']);

      const isDeliverooEnabled =
        isDelivery && params.long && params.lat && get(settings, 'isGoogleMapsEnabled') && !isEmpty(deliverooSignature);

      const payload = await tenantApi.get('stores/tenant', {
        params: {
          ...params,
          weekday: timeUtils.getCurrentWeekday(),
        },
        paramsSerializer: params => {
          return qs.stringify(params, { arrayFormat: 'brackets' });
        },
        skipAuth: true,
      });

      const availableStores = isDeliverooEnabled
        ? await getDeliverooStores({
            stores: payload,
            deliverooSignature,
            lon: params.long,
            lat: params.lat,
          })
        : payload;

      setStores(stores => (params.offset ? stores.concat(payload) : payload));
      setDeliverooStores(stores => (params.offset ? stores.concat(availableStores) : availableStores));
      setIsDeliveroo(isDeliverooEnabled);

      return availableStores;
    },
    [isDelivery]
  );

  const clearStoreMenus = useCallback(() => setMenus({}), [setMenus]);

  const getStoresMenus = useCallback(async ({ stores, locale, brands }) => {
    const menus = await catalogApi.get('menu/stores', {
      params: { stores, brands },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'brackets' });
      },
      headers: { locale },
    });

    setMenus(m => ({ ...m, ...menus }));
  }, []);

  return {
    clearStoreMenus,
    getStores,
    getStoresMenus,
    menus,
    stores: isDeliveroo ? deliverooStores : stores,
    storesAvailability,
    storesCount: stores.length,
  };
};
